import { relations, sql } from "drizzle-orm";
import { boolean, date, integer, jsonb, numeric, pgTable, text, timestamp, uniqueIndex, uuid, } from "drizzle-orm/pg-core";
import { dossierDocument } from "./dossier.js";
import { lender } from "./main.js";
export const inboundEmailTable = pgTable("inbound_email", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    from: text("from").notNull(),
    to: text("to").notNull(),
    subject: text("subject"),
    postmarkMessageId: text("postmark_message_id").notNull().unique(),
    postmarkData: jsonb("postmark_data").$type(),
    receivedAt: timestamp("received_at", {
        withTimezone: true,
        mode: "string",
    }).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const inboundEmailRelations = relations(inboundEmailTable, ({ one }) => ({
    interestEmail: one(interestEmailTable, {
        fields: [inboundEmailTable.id],
        references: [interestEmailTable.inboundEmailId],
    }),
}));
export const interestEmailTable = pgTable("interest_email", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    from: text("from").notNull(),
    lenderId: text("lender_id"),
    isInterest: boolean("is_interest"),
    interestSheetLinks: jsonb("interest_sheet_links").$type(),
    interestSheetAttachmentIndices: jsonb("interest_sheet_attachment_indices").$type(),
    subject: text("subject"),
    inboundEmailId: uuid("inbound_email_id").notNull().unique(),
    receivedAt: timestamp("received_at", {
        withTimezone: true,
        mode: "string",
    }).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const interestEmailRelations = relations(interestEmailTable, ({ one, many }) => ({
    inboundEmail: one(inboundEmailTable, {
        fields: [interestEmailTable.inboundEmailId],
        references: [inboundEmailTable.id],
    }),
    lender: one(lender, {
        fields: [interestEmailTable.lenderId],
        references: [lender.id],
    }),
    interestSheets: many(interestSheet),
}));
export const interestSheet = pgTable("interest_sheet", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    interestEmailId: uuid("interest_email_id")
        .notNull()
        .references(() => interestEmailTable.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    lenderId: text("lender_id"),
    documentId: uuid("document_id")
        .notNull()
        .unique()
        .references(() => dossierDocument.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    sourceType: text("source_type").notNull().$type(),
    sourceAttachmentIndex: integer("source_attachment_index"),
    sourceUrl: text("source_url"),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const interestSheetDetail = pgTable("interest_sheet_detail", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    interestSheetId: uuid("interest_sheet_id")
        .notNull()
        .unique()
        .references(() => interestSheet.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    analysisModel: text("analysis_model").notNull(),
    analysisApiVersion: text("analysis_api_version").notNull(),
    analysisResult: jsonb("analysis_result").$type(),
    analyzedAt: timestamp("analyzed_at", {
        withTimezone: true,
        mode: "string",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const interestSheetRate = pgTable("interest_sheet_rate", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    interestSheetId: uuid("interest_sheet_id")
        .notNull()
        .references(() => interestSheet.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    lenderId: text("lender_id"),
    productName: text("product_name").notNull(),
    repaymentForm: text("repayment_form")
        .notNull()
        .$type(),
    fixedRateMonthsMin: integer("fixed_rate_months_min"),
    fixedRateMonthsMax: integer("fixed_rate_months_max"),
    nhg: boolean("nhg").notNull(),
    ltvMinPct: integer("ltv_min_pct"),
    ltvMaxPct: integer("ltv_max_pct"),
    ratePct: numeric("rate_pct", { precision: 5, scale: 3 }),
    effectiveDate: date("effective_date", { mode: "string" }).notNull(),
    needsReview: boolean("needs_review").notNull().default(false),
    sourceFragment: text("source_fragment"),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (t) => [
    // Partiele unieke index: alleen schone rijen (needs_review = false) sluiten
    // elkaar uit. Fallout-rijen (needs_review = true) vallen buiten de index en
    // mogen vrij naast elkaar én naast schone rijen bestaan. NULLS NOT DISTINCT
    // zodat NULL-bevattende sleutels (open-eind/gesloten LTV, variabele rente)
    // alsnog botsen tussen schone rijen. drizzle-orm 0.45.1 heeft geen
    // .nullsNotDistinct() op uniqueIndex(); de NULLS NOT DISTINCT-clausule wordt
    // in de migratie-SQL toegevoegd (zie 0111). Deze declaratie houdt de
    // kolomvolgorde + WHERE-conditie in het schema; de drift op enkel het
    // NULLS-NOT-DISTINCT-woord is bekend en wordt in de migratie geborgd.
    uniqueIndex("interest_sheet_rate_natural_key")
        .on(t.interestSheetId, t.productName, t.repaymentForm, t.fixedRateMonthsMin, t.fixedRateMonthsMax, t.nhg, t.ltvMinPct, t.ltvMaxPct)
        .where(sql `${t.needsReview} = false`),
]);
export const interestSheetRateRelations = relations(interestSheetRate, ({ one }) => ({
    interestSheet: one(interestSheet, {
        fields: [interestSheetRate.interestSheetId],
        references: [interestSheet.id],
    }),
    lender: one(lender, {
        fields: [interestSheetRate.lenderId],
        references: [lender.id],
    }),
}));
export const interestSheetRelations = relations(interestSheet, ({ one, many }) => ({
    rates: many(interestSheetRate),
    interestEmail: one(interestEmailTable, {
        fields: [interestSheet.interestEmailId],
        references: [interestEmailTable.id],
    }),
    lender: one(lender, {
        fields: [interestSheet.lenderId],
        references: [lender.id],
    }),
    document: one(dossierDocument, {
        fields: [interestSheet.documentId],
        references: [dossierDocument.id],
    }),
    detail: one(interestSheetDetail, {
        fields: [interestSheet.id],
        references: [interestSheetDetail.interestSheetId],
    }),
}));
export const interestSheetDetailRelations = relations(interestSheetDetail, ({ one }) => ({
    interestSheet: one(interestSheet, {
        fields: [interestSheetDetail.interestSheetId],
        references: [interestSheet.id],
    }),
}));
